import type { EditorProps } from "../editor.types";

/**
 * Builds the Monaco `IStandaloneEditorConstructionOptions` from component props.
 * Hidden chrome by default (no line numbers, no scrollbars, no folding,
 * word-wrapped at 75 columns).
 *
 * @param props - The `<e-editor>` component props.
 * @param value - Initial text content for the editor model.
 * @param language - Monaco language id (e.g. `"typescript"`, `"json"`).
 * @param theme - Resolved Monaco theme id from {@link resolveTheme}.
 */
export const buildEditorOptions = (
  props: Readonly<EditorProps>,
  value: string,
  language: string,
  theme: string,
): monaco.editor.IStandaloneEditorConstructionOptions => {
  const isReadOnly = props.readOnly ?? false;

  return {
    value,
    language,
    theme,
    readOnly: isReadOnly,
    minimap: { enabled: props.minimap ?? false },
    automaticLayout: props.automaticLayout ?? false,
    padding: { top: 0, bottom: 0 },
    lineNumbers: "off",
    wordWrap: "off",
    folding: false,
    renderLineHighlight: "none",
    renderFinalNewline: "off",
    scrollBeyondLastLine: false,
    scrollbar: { vertical: "hidden", horizontal: "auto", handleMouseWheel: false },
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    domReadOnly: isReadOnly || undefined,
    accessibilityHelpUrl: props.accessibilityHelpUrl,
    ariaContainerElement: props.ariaContainerElement ?? document.body,

    // Disable features that slow down initialization
    contextmenu: false,
    quickSuggestions: false,
    suggestOnTriggerCharacters: false,
    parameterHints: { enabled: false },
    hover: { enabled: false },
    links: false,
    colorDecorators: false,
    matchBrackets: "never",
    occurrencesHighlight: "off",
    selectionHighlight: false,
    codeLens: false,
    lightbulb: { enabled: "off" as const },
    bracketPairColorization: { enabled: false },
    guides: { indentation: false, bracketPairs: false },
    renderWhitespace: "none",
    stickyScroll: { enabled: false },
    inlayHints: { enabled: "off" as const },
    inlineSuggest: { enabled: false },
    fontLigatures: false,
  };
};
